import { useState, useEffect } from 'react'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db } from '../services/firebase'
import { useAuth } from '../contexts/AuthContext'
import { BrCard, BrButton } from '@govbr-ds/react-components'

interface ExpertData {
    emails: string[]
    updatedAt?: string
    updatedBy?: string
}

export function ExpertsManagementPage() {
    const { user } = useAuth()
    const [experts, setExperts] = useState<string[]>([])
    const [newEmail, setNewEmail] = useState('')
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

    // Load experts from Firestore
    useEffect(() => {
        loadExperts()
    }, [])

    const loadExperts = async () => {
        try {
            setLoading(true)
            const docRef = doc(db, 'roles', 'experts')
            const docSnap = await getDoc(docRef)

            if (docSnap.exists()) {
                const data = docSnap.data() as ExpertData
                setExperts(data.emails || [])
            } else {
                setExperts([])
            }
        } catch (error) {
            console.error('Error loading experts:', error)
            setMessage({ type: 'error', text: 'Erro ao carregar especialistas' })
        } finally {
            setLoading(false)
        }
    }

    const saveExperts = async (newExperts: string[]) => {
        try {
            setSaving(true)
            const docRef = doc(db, 'roles', 'experts')
            await setDoc(docRef, {
                emails: newExperts,
                updatedAt: new Date().toISOString(),
                updatedBy: user?.email || 'unknown'
            })
            setExperts(newExperts)
            setMessage({ type: 'success', text: 'Lista atualizada com sucesso!' })
            setTimeout(() => setMessage(null), 3000)
        } catch (error) {
            console.error('Error saving experts:', error)
            setMessage({ type: 'error', text: 'Erro ao salvar. Verifique permissões do Firestore.' })
        } finally {
            setSaving(false)
        }
    }

    const addExpert = () => {
        const email = newEmail.trim().toLowerCase()

        if (!email) {
            setMessage({ type: 'error', text: 'Digite um email válido' })
            return
        }

        if (!email.includes('@')) {
            setMessage({ type: 'error', text: 'Email inválido' })
            return
        }

        if (experts.includes(email)) {
            setMessage({ type: 'error', text: 'Este email já está na lista' })
            return
        }

        saveExperts([...experts, email])
        setNewEmail('')
    }

    const removeExpert = (emailToRemove: string) => {
        if (confirm(`Remover ${emailToRemove} da lista de especialistas?`)) {
            saveExperts(experts.filter(e => e !== emailToRemove))
        }
    }

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            addExpert()
        }
    }

    if (loading) {
        return (
            <div className="d-flex align-items-center justify-content-center py-12">
                <div className="spinner"></div>
            </div>
        )
    }

    return (
        <div className="max-w-3xl mx-auto">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-80">Gestão de Especialistas</h1>
                <p className="text-gray-60 mt-1">
                    Gerencie os emails dos especialistas que podem acessar o sistema de avaliação IQAI.
                </p>
            </div>

            {/* Message */}
            {message && (
                <div className={`mb-4 p-4 rounded-lg ${message.type === 'success'
                    ? 'bg-success text-success border border-success'
                    : 'bg-danger text-danger border border-danger'
                    }`}>
                    {message.text}
                </div>
            )}

            {/* Add Expert Form */}
            <BrCard className="mb-6">
                <div className="card-header"><span className="title">Adicionar Especialista</span></div>
                <div className="card-content">
                    <div className="d-flex gap-3">
                        <input
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="email@exemplo.com"
                            className="flex-1 px-4 py-2 border border-gray-20 rounded-lg focus:ring-2 focus:ring-primary-default focus:border-primary-default"
                            disabled={saving}
                        />
                        <BrButton
                            primary
                            onClick={addExpert}
                            disabled={saving || !newEmail.trim()}
                            className={saving ? 'opacity-50 cursor-not-allowed' : ''}
                        >
                            {saving ? 'Salvando...' : 'Adicionar'}
                        </BrButton>
                    </div>
                    <p className="text-xs text-gray-60 mt-2">
                        Pressione Enter ou clique em Adicionar para incluir o email na lista.
                    </p>
                </div>
            </BrCard>

            {/* Experts List */}
            <BrCard>
                <div className="card-header"><span className="title">Especialistas Cadastrados ({experts.length})</span></div>
                <div className="card-content">
                    {experts.length === 0 ? (
                        <div className="text-center py-8 text-gray-60">
                            <p className="text-lg mb-2">Nenhum especialista cadastrado</p>
                            <p className="text-sm">Adicione emails acima para permitir acesso ao sistema de avaliação.</p>
                        </div>
                    ) : (
                        <ul className="list-none p-0">
                            {experts.sort().map((email, index) => (
                                <li key={index} className="py-3 d-flex align-items-center justify-content-between border-b border-gray-10 group">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="w-8 h-8 rounded-full d-flex align-items-center justify-content-center bg-primary-default text-pure-0 font-medium text-sm" style={{ width: '2rem', height: '2rem' }}>
                                            {email.charAt(0).toUpperCase()}
                                        </div>
                                        <span className="text-gray-80">{email}</span>
                                    </div>
                                    <button
                                        onClick={() => removeExpert(email)}
                                        className="text-danger hover-text-danger px-3 py-1 rounded hover-bg-danger transition-colors border-0 bg-transparent cursor-pointer"
                                        disabled={saving}
                                    >
                                        Remover
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </BrCard>

            {/* Info */}
            <div className="mt-6 p-4 bg-info rounded-lg border border-info" style={{ backgroundColor: 'rgba(21, 91, 203, 0.1)' }}>
                <h3 className="font-medium text-info mb-2">ℹ️ Como funciona</h3>
                <ul className="text-sm text-info space-y-1 list-none p-0">
                    <li>• Especialistas cadastrados aqui podem acessar o fluxo de avaliação IQAI.</li>
                    <li>• Ao fazer login com um email da lista, o usuário é redirecionado para /especialista.</li>
                    <li>• Usuários não cadastrados são tratados como participantes.</li>
                    <li>• Alterações entram em vigor imediatamente após salvar.</li>
                </ul>
            </div>
        </div>
    )
}
